import { supabase } from '@/utils/supabase';
import { lancarErroAdmin } from '@/utils/errosAdmin';
import {
  registrarCatalogo,
  type EscopoCatalogo,
  type ModuloCatalogo,
  type RecursoCatalogo,
} from '@/utils/adminPermissoes';

/** Situação real do segundo fator do operador (decidida no servidor). */
export interface MfaOperador {
  exigido: boolean;
  configurado: boolean;
  motivo: string | null;
}

/** Operador autenticado no Console Administrativo PETBEA. */
export interface OperadorAdmin {
  user_id: string;
  nome: string | null;
  email: string | null;
  avatar_url?: string | null;
  papel_codigo: string;
  papel_nome: string | null;
  status: string;
  escopos: string[];
  escopo_modo?: string;
  mfa?: MfaOperador;
  mfa_obrigatorio: boolean;
  mfa_habilitado: boolean;
  politica_aceita_em: string | null;
  senha_provisoria?: boolean;
  ultimo_acesso?: string | null;
}

export interface ResultadoLoginAdmin {
  ok: boolean;
  access_token: string;
  refresh_token: string;
  mfa_requerido: boolean;
  /** true quando o perfil exige MFA e o operador ainda não cadastrou o TOTP. */
  mfa_cadastro_pendente?: boolean;
  operador: OperadorAdmin;
}

export interface MeuAcessoAdmin {
  operador: OperadorAdmin;
  permissoes: string[];
  /** Exceções individuais vigentes (CONCEDER/NEGAR) do próprio operador. */
  excecoes?: ExcecaoPermissao[];
}

/** Exceção individual de permissão aplicada a um operador. */
export interface ExcecaoPermissao {
  id: string;
  recurso: string;
  acao: string;
  efeito: 'CONCEDER' | 'NEGAR';
  motivo: string;
  expira_em: string | null;
}

/** Exceção individual como devolvida por `listar_excecoes`. */
export interface ExcecaoOperador {
  id: string;
  user_id: string;
  permissao_id: string;
  recurso: string | null;
  acao: string | null;
  efeito: 'CONCEDER' | 'NEGAR';
  motivo: string;
  expira_em: string | null;
  created_at: string;
}

export interface EventoAcessoAdmin {
  id: string;
  acao: string;
  descricao: string | null;
  ip_origem: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface OperadorListado {
  user_id: string;
  nome: string | null;
  email: string | null;
  papel_codigo: string;
  escopos: string[] | null;
  escopo_modo?: string | null;
  status: string;
  mfa_obrigatorio: boolean;
  mfa_habilitado: boolean;
  senha_provisoria?: boolean;
  ultimo_acesso: string | null;
  created_at: string;
}

export interface PapelAdmin {
  codigo: string;
  nome: string;
  descricao: string | null;
  nivel: number;
  /** Colunas novas (migration 2026-10-18). */
  escopo_todos?: boolean | null;
  escopos_padrao?: string[] | null;
  mfa_exigido?: boolean | null;
  privilegiado?: boolean | null;
  papel_pcapi_sugerido?: string | null;
  resumo?: string | null;
  operadores_ativos?: number;
}

/** Recorte de um perfil que não bate com o padrão. */
export interface DivergenciaPerfil {
  papel_codigo: string;
  faltando: number;
  a_mais: number;
}

/** Par proibido pela segregação de funções. */
export interface ParSegregacao {
  permissao_a: string;
  permissao_b: string;
  motivo: string;
}

/** Resposta ampliada de `listar_papeis` (Prompt 20A). */
export interface RespostaPapeisCompleta {
  papeis: PapelAdmin[];
  permissoes: PermissaoAdmin[];
  mapa: { papel_codigo: string; permissao_id: string }[];
  divergencias: DivergenciaPerfil[];
  modulos: ModuloCatalogo[];
  recursos: RecursoCatalogo[];
  escopos: EscopoCatalogo[];
  padrao: { papel_codigo: string; permissao_id: string }[];
  sod: ParSegregacao[];
}

/** Linha de `petbea_acesso_detalhado` (perfil × exceção × escopo × efetiva). */
export interface DetalheAcessoLinha {
  permissao_id: string;
  recurso: string;
  acao: string;
  modulo_codigo: string | null;
  escopo_codigo: string | null;
  no_perfil: boolean;
  excecao: 'CONCEDER' | 'NEGAR' | null;
  excecao_motivo: string | null;
  excecao_expira_em: string | null;
  dentro_escopo: boolean;
  efetiva: boolean;
  origem: string | null;
}

export interface PermissaoAdmin {
  id: string;
  recurso: string;
  acao: string;
  descricao: string | null;
}

export interface LogAuditoria {
  id: string;
  user_id: string | null;
  user_email: string | null;
  user_nome: string | null;
  user_role: string | null;
  acao: string;
  entidade: string | null;
  descricao: string | null;
  ip_origem: string | null;
  canal: string | null;
  created_at: string;
}

/**
 * Invoca a função de borda `admin-auth`.
 * As recusas chegam como `{ codigo, detail }` — o código é preservado em ErroAdmin
 * para a tela reagir (mfa_requerido, proprio_acesso, ultimo_superadmin, segregacao_funcoes).
 */
async function invocar<T>(payload: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('admin-auth', { body: payload });
  if (error) await lancarErroAdmin(error);
  const resultado = data as (T & { error?: string }) | null;
  if (resultado?.error) throw new Error(resultado.error);
  return resultado as T;
}

/** Autentica o operador e devolve a sessão (mesma sessão Supabase do PETBEA). */
export async function loginAdmin(email: string, senha: string): Promise<ResultadoLoginAdmin> {
  return invocar<ResultadoLoginAdmin>({ acao: 'login', email, password: senha });
}

/** Carrega o vínculo de operador + as permissões granulares da sessão atual. */
export async function carregarMeuAcesso(): Promise<MeuAcessoAdmin> {
  return invocar<MeuAcessoAdmin>({ acao: 'me' });
}

/** Registra o logout na trilha de auditoria. */
export async function registrarLogoutAdmin(): Promise<void> {
  try {
    await invocar<{ ok: boolean }>({ acao: 'logout' });
  } catch {
    /* o logout local não deve falhar por causa da auditoria */
  }
}

/** Registra um evento de acesso (MFA, redefinição de senha…). */
export async function registrarEventoAdmin(evento: string, contexto?: string): Promise<void> {
  try {
    await invocar<{ ok: boolean }>({ acao: 'registrar_evento', evento, contexto });
  } catch {
    /* eventos auxiliares não interrompem o fluxo */
  }
}

/** Eventos recentes de acesso do próprio operador (sessões/atividade). */
export async function meusEventosAdmin(): Promise<EventoAcessoAdmin[]> {
  const r = await invocar<{ eventos: EventoAcessoAdmin[] }>({ acao: 'meus_eventos' });
  return r.eventos ?? [];
}

/** Lista os operadores administrativos (requer USUARIOS:visualizar). */
export async function listarOperadoresAdmin(): Promise<OperadorListado[]> {
  const r = await invocar<{ operadores: OperadorListado[] }>({ acao: 'listar_operadores' });
  return r.operadores ?? [];
}

/** Lista papéis, permissões e o mapa papel × permissão (requer USUARIOS:visualizar). */
export async function listarPapeisAdmin(): Promise<{
  papeis: PapelAdmin[];
  permissoes: PermissaoAdmin[];
  mapa: { papel_codigo: string; permissao_id: string }[];
}> {
  return invocar({ acao: 'listar_papeis' });
}

/** Consulta a trilha de auditoria (requer AUDITORIA:visualizar). */
export async function listarAuditoriaAdmin(evento?: string, limite = 60): Promise<LogAuditoria[]> {
  const r = await invocar<{ logs: LogAuditoria[] }>({ acao: 'listar_auditoria', evento, limite });
  return r.logs ?? [];
}

/**
 * Define perfil, status, escopo e MFA de um operador.
 * `escopo_modo`: PERFIL (herda do perfil) | TODOS | RESTRITO (exige ao menos 1 escopo).
 * `mfa_reforco`: só é enviado quando verdadeiro — nunca liga o MFA por omissão.
 */
export async function definirOperadorAdmin(payload: {
  user_id: string;
  papel_codigo: string;
  status: string;
  escopo_modo: 'PERFIL' | 'TODOS' | 'RESTRITO';
  escopos: string[];
  mfa_reforco: boolean;
}): Promise<void> {
  await invocar<{ ok: boolean }>({
    acao: 'definir_operador',
    user_id: payload.user_id,
    papel_codigo: payload.papel_codigo,
    status: payload.status,
    escopo_modo: payload.escopo_modo,
    escopos: payload.escopo_modo === 'RESTRITO' ? payload.escopos : [],
    mfa_reforco: payload.mfa_reforco === true,
  });
}

/**
 * Remove o vínculo de operador do Console (a identidade PETBEA é preservada).
 * Requer USUARIOS:alterar_permissoes.
 */
export async function removerOperadorAdmin(userId: string): Promise<void> {
  await invocar<{ ok: boolean }>({ acao: 'remover_operador', user_id: userId });
}

/** URL de retorno do convite — tela de primeiro acesso do operador. */
export function urlPrimeiroAcesso(): string {
  const base = __BASE_PATH__.split('/').filter(Boolean).join('/');
  const prefix = base ? `/${base}` : '';
  return `${window.location.origin}${prefix}/admin/primeiro-acesso`;
}

/** Convida um novo operador por e-mail (requer USUARIOS:criar). */
export async function convidarOperadorAdmin(payload: {
  email: string;
  nome: string;
  papel_codigo: string;
  escopos: string[];
  escopo_modo?: 'PERFIL' | 'TODOS' | 'RESTRITO';
  mfa_reforco?: boolean;
  /** Compatibilidade: tratado como mfa_reforco. */
  mfa_obrigatorio?: boolean;
  permitir_externo?: boolean;
}): Promise<void> {
  const { mfa_obrigatorio, mfa_reforco, escopo_modo, ...resto } = payload;
  await invocar<{ ok: boolean }>({
    acao: 'convidar_operador',
    redirect_to: urlPrimeiroAcesso(),
    ...resto,
    // Escopos escolhidos na tela significam modo RESTRITO (senão o servidor
    // descartaria a lista e o perfil resolveria tudo sozinho).
    escopo_modo: escopo_modo ?? ((resto.escopos ?? []).length > 0 ? 'RESTRITO' : 'PERFIL'),
    mfa_reforco: mfa_reforco === true || mfa_obrigatorio === true,
  });
}

/** Reenvia o convite de um operador ainda não confirmado (requer USUARIOS:criar). */
export async function reenviarConviteAdmin(userId: string): Promise<void> {
  await invocar<{ ok: boolean }>({
    acao: 'reenviar_convite',
    user_id: userId,
    redirect_to: urlPrimeiroAcesso(),
  });
}

/**
 * Cria um operador do zero com a ficha completa (nome, e-mail, CPF, telefone,
 * data de nascimento, papel, escopos e, opcionalmente, vínculo de agente público).
 * O acesso é liberado com senha provisória e troca obrigatória no primeiro acesso.
 * Requer USUARIOS:criar.
 */
export async function criarOperadorAdmin(payload: {
  nome: string;
  email: string;
  cpf?: string;
  telefone?: string;
  data_nascimento?: string;
  papel_codigo: string;
  escopos: string[];
  escopo_modo?: 'PERFIL' | 'TODOS' | 'RESTRITO';
  mfa_reforco?: boolean;
  /** Compatibilidade: tratado como mfa_reforco. */
  mfa_obrigatorio?: boolean;
  senha: string;
  permitir_externo?: boolean;
  matricula?: string;
  orgao?: string;
  cargo?: string;
  agente_role?: string;
}): Promise<{ user_id: string }> {
  const { mfa_obrigatorio, mfa_reforco, escopo_modo, ...resto } = payload;
  return invocar<{ ok: boolean; user_id: string }>({
    acao: 'criar_operador',
    ...resto,
    escopo_modo: escopo_modo ?? ((resto.escopos ?? []).length > 0 ? 'RESTRITO' : 'PERFIL'),
    mfa_reforco: mfa_reforco === true || mfa_obrigatorio === true,
  });
}

/** Marca a senha provisória como substituída no primeiro acesso do próprio operador. */
export async function alterarSenhaProvisoriaAdmin(): Promise<void> {
  await invocar<{ ok: boolean }>({ acao: 'alterar_senha_provisoria' });
}

/** Conclui o primeiro acesso do operador (senha definida + política aceita). */
export async function concluirPrimeiroAcesso(): Promise<void> {
  await invocar<{ ok: boolean }>({ acao: 'primeiro_acesso' });
}

/**
 * Sincroniza o estado do MFA do próprio operador com o Auth.
 * O valor gravado nunca é autodeclarado: o servidor relê os fatores TOTP.
 */
export async function sincronizarMfa(): Promise<{ mfa: MfaOperador; mfa_habilitado: boolean }> {
  return invocar<{ ok: boolean; mfa: MfaOperador; mfa_habilitado: boolean }>({
    acao: 'mfa_sincronizar',
  });
}

/* ------------------------------------------------------------------ */
/* PERMISSÕES POR ITEM + EXCEÇÕES INDIVIDUAIS                          */
/* ------------------------------------------------------------------ */

/** Conjunto completo papéis × permissões (matriz de gestão). */
export interface MatrizPermissoesAdmin {
  papeis: PapelAdmin[];
  permissoes: PermissaoAdmin[];
  mapa: { papel_codigo: string; permissao_id: string }[];
}

/** Carrega a matriz papéis × permissões (requer USUARIOS:alterar_permissoes). */
export async function carregarMatrizPermissoes(): Promise<MatrizPermissoesAdmin> {
  const r = await invocar<MatrizPermissoesAdmin & { ok?: boolean }>({ acao: 'listar_matriz' });
  return {
    papeis: r.papeis ?? [],
    permissoes: r.permissoes ?? [],
    mapa: r.mapa ?? [],
  };
}

/** Concede ou remove UMA permissão de um papel. Nunca altera o estado local antes da resposta. */
export async function definirPermissaoPapel(payload: {
  papel_codigo: string;
  permissao_id: string;
  conceder: boolean;
}): Promise<void> {
  await invocar<{ ok: boolean }>({ acao: 'definir_permissao_papel', ...payload });
}

/** Lista as exceções individuais de um operador. */
export async function listarExcecoesAdmin(userId: string): Promise<ExcecaoOperador[]> {
  const r = await invocar<{ excecoes: ExcecaoOperador[] }>({ acao: 'listar_excecoes', user_id: userId });
  return r.excecoes ?? [];
}

/** Cria/atualiza a exceção individual (CONCEDER ou NEGAR) de um operador. */
export async function definirExcecaoAdmin(payload: {
  user_id: string;
  permissao_id: string;
  efeito: 'CONCEDER' | 'NEGAR';
  motivo: string;
  expira_em?: string | null;
}): Promise<void> {
  await invocar<{ ok: boolean }>({ acao: 'definir_excecao', ...payload });
}

/** Remove uma exceção individual (o motivo entra na trilha de auditoria). */
export async function removerExcecaoAdmin(id: string, motivo: string): Promise<void> {
  await invocar<{ ok: boolean }>({ acao: 'remover_excecao', id, motivo });
}

/* ------------------------------------------------------------------ */
/* PERFIS COM ESCOPO + MFA (Prompt 20B)                                */
/* ------------------------------------------------------------------ */

/**
 * Catálogo completo de perfis: papéis com as colunas novas, divergências em
 * relação ao padrão, módulos, recursos, escopos, padrão e segregação.
 * Os rótulos e ordens passam a vir do banco (petbea_admin_recursos/_modulos).
 */
export async function listarPapeisCompleto(): Promise<RespostaPapeisCompleta> {
  const r = await invocar<
    Partial<RespostaPapeisCompleta> & { papeis?: PapelAdmin[]; permissoes?: PermissaoAdmin[]; mapa?: { papel_codigo: string; permissao_id: string }[] }
  >({ acao: 'listar_papeis' });

  const recursos = r.recursos ?? [];
  const modulos = r.modulos ?? [];
  const escopos = r.escopos ?? [];
  registrarCatalogo({ recursos, modulos, escopos });

  return {
    papeis: r.papeis ?? [],
    permissoes: r.permissoes ?? [],
    mapa: r.mapa ?? [],
    divergencias: r.divergencias ?? [],
    modulos,
    recursos,
    escopos,
    padrao: r.padrao ?? [],
    sod: r.sod ?? [],
  };
}

/**
 * Detalha o acesso de um operador (perfil × exceção × escopo × efetiva).
 * O próprio usuário pode consultar o seu; os demais exigem USUARIOS:visualizar.
 */
export async function detalharAcesso(userId: string): Promise<DetalheAcessoLinha[]> {
  const r = await invocar<{ ok: boolean; detalhe: DetalheAcessoLinha[] }>({
    acao: 'detalhar_acesso',
    user_id: userId,
  });
  return r.detalhe ?? [];
}

/** Restaura o padrão do perfil (zera a divergência). Exige motivo. */
export async function restaurarPerfil(
  papelCodigo: string,
  motivo: string,
): Promise<{ removidas: number; incluidas: number }> {
  const r = await invocar<{ ok: boolean; removidas: number; incluidas: number }>({
    acao: 'restaurar_perfil',
    papel_codigo: papelCodigo,
    motivo,
  });
  return { removidas: Number(r.removidas ?? 0), incluidas: Number(r.incluidas ?? 0) };
}

/* ------------------------------------------------------------------ */
/* HELPERS DE LEITURA                                                  */
/* ------------------------------------------------------------------ */

/**
 * Nível de acesso de um conjunto de permissões dentro de um recurso:
 * 0 = nenhum, 1 = leitura, 2 = leitura e escrita, 3 = total.
 */
/** Nível de acesso a um recurso: 0 nenhum, 1 leitura, 2 leitura+escrita, 3 total. */
export type NivelAcesso = 0 | 1 | 2 | 3;

export function nivelDoRecurso(permissoes: Set<string> | string[], recurso: string): NivelAcesso {
  const lista = Array.isArray(permissoes) ? permissoes : Array.from(permissoes);
  const doRecurso = lista.filter((p) => p.startsWith(`${recurso}:`));
  if (doRecurso.length === 0) return 0;
  const acoes = doRecurso.map((p) => p.split(':')[1]);
  const total = acoes.includes('aprovar') && acoes.includes('excluir');
  if (total) return 3;
  const escrita = acoes.some((a) => a !== 'visualizar' && a !== 'exportar');
  if (escrita) return 2;
  return 1;
}

export const ROTULO_NIVEL: Record<0 | 1 | 2 | 3, string> = {
  0: 'Sem acesso',
  1: 'Leitura',
  2: 'Leitura e escrita',
  3: 'Total',
};